import { BaseWebComponentController, IListenerConfig } from '../../../../framework/BaseWebComponentController';
import { templateProductTrialsApp } from '../view/template';
import { InProductTrialsAppWebComponent } from '../component/InProductTrialsAppWebComponent';
import { InProductTrialsComplianceContactUs } from '../component/complianceManager/InProductTrialsComplianceContactUs';
import { InProductTrialsAppView } from '../view/InProductTrialsAppView';
import { InProductTrialsAppRepository } from '../repository/InProductTrialsAppRepository';
import { HomePageEventsName } from '../../gzTrialHomePage/types';
import { InProductTrialsAppViewData } from '../types/AvailableInProductTrialsDetails';
import { AvailableProductTrialType } from '../../types/AvailableProductTrialType';
import { LandingPageEventsName } from '../../../common/gzLandingPage/types';
import { GzLandingPageButtonId } from '../../../common/gzLandingPage/view/gzLandingPageConfig/gz-landing-page-button-id';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { TemplateState } from '../../../../types/common/types';
import { CoolDownPeriodMonths } from '../../types/InProductTrialCannotBeStartedReason';
import {
  getGzLandingPageProductLinkWithTracking,
  IN_PRODUCT_TRIALS_TRACKING_NAME,
} from '../../tracking/in-product-trials-tracking';
import { GzInProductTrialsVideoWebComponent } from '../../../common/gzTrialVideo';
import { GzLandingPageDemoLinks } from '../../../common/gzLandingPage/view/gzLandingPageConfig/gz-landing-page-links';
import { getTranslatedInProductTrialType } from '../../utils/InProductTrialType';
import { InProductTrialTypeEnum } from '@nirvana/shared/dtos/types/in-product-trials';
import { translateFn } from '../../../../utils/translator.helper';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { GzDialog } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialog';

export class InProductTrialsAppController extends BaseWebComponentController {
  public component: InProductTrialsAppWebComponent;
  public view: InProductTrialsAppView;
  public viewConstructor: typeof InProductTrialsAppView;
  public dataRepository: InProductTrialsAppRepository;
  public dataRepositoryConstructor: typeof InProductTrialsAppRepository;

  constructor(component: InProductTrialsAppWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: InProductTrialsAppView,
      viewTemplate: templateProductTrialsApp,
      dataRepositoryConstructor: InProductTrialsAppRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$gzTrialHomePage,
        event: HomePageEventsName.PRODUCT_ITEM,
        callback: (event: CustomEvent<{ type: AvailableProductTrialType }>) => {
          NavigationHelper.navigateToProductsHub(event.detail.type);
        },
      },
      {
        element: this.view.dom.$gzLandingPage,
        event: LandingPageEventsName.PRODUCT_ITEM,
        callback: async (
          event: CustomEvent<{
            target: GzButtonWebComponent;
            type: AvailableProductTrialType;
          }>
        ) => {
          await this.handleGzLandingPageButtonClicked(event.detail.target, event.detail.type);
        },
      },
    ];
  }

  async getDataForView(): Promise<InProductTrialsAppViewData> {
    this.view.setWrapperTemplateState(TemplateState.LOADING);

    return {
      availableInProductTrialsDetails: await this.dataRepository.getAvailableInProductTrials(),
      buyProductCardDataProvider: {
        fetchProductBuyInitialData: this.dataRepository.fetchProductBuyInitialData.bind(this.dataRepository),
        fetchProductBuyPrice: this.dataRepository.fetchProductBuyPrice.bind(this.dataRepository),
        fetchProductBuyUrl: this.dataRepository.fetchProductBuyUrl.bind(this.dataRepository),
      },
    };
  }

  private async refreshLandingPageForTrialType(type: AvailableProductTrialType) {
    try {
      const availableInProductTrialsDetails = await this.dataRepository.getAvailableInProductTrials();
      this.view.refreshLandingPageForTrialType(availableInProductTrialsDetails, type);
    } catch (e) {
      // if inProductTrials feature becomes unavailable, just log an error, because window will be reloaded
      console.error(e);
    }
  }

  public showGzLandingPageForProductTrial(type: AvailableProductTrialType) {
    // track action: Show in product trials landing page
    analytics.trackPageView({
      parentName: IN_PRODUCT_TRIALS_TRACKING_NAME,
      name: getTranslatedInProductTrialType(type),
    });

    this.view.showGzLandingPageForProductTrial(type);
  }

  private async handleGzLandingPageButtonClicked(target: GzButtonWebComponent, type: AvailableProductTrialType) {
    switch (target.id) {
      case GzLandingPageButtonId.FreeTrial:
        await this.startInProductTrial(target, type);
        return;
      case GzLandingPageButtonId.StopTrial:
        await this.showStopTrialConfirmationWindow(type);
        return;
      case GzLandingPageButtonId.HowTo:
        this.openHowToLinkInProductTrial(type);
        return;
      case GzLandingPageButtonId.ViewDemo:
        this.showDemoForInProductTrial(type);
        return;
      case GzLandingPageButtonId.RequestTrial:
        await this.requestInProductTrial(target, type);
        return;
    }
  }

  private async startInProductTrial(target: GzButtonWebComponent, type: AvailableProductTrialType) {
    try {
      // set loading state
      target.setLoadingState(true);

      // call start in product trial rpc
      const response = await this.dataRepository.startInProductTrial(type);

      if (response.success) {
        // show success toast and refresh landing page
        await this.refreshLandingPageForTrialType(type);
        this.showSuccessToast(
          this.dependencies.translateFn('{productName} trial started.', {
            productName: getTranslatedInProductTrialType(type),
          })
        );

        this.reloadWindow();
      } else {
        let message: string | undefined;

        if (response.exceptionType === 'CannotStartInProductTrial') {
          message = translateFn(
            'The trial requires you to remove the following incompatible products: {ENUMERATED_PRODUCT_NAMES}. To proceed, open the user menu, select My Company, and then select the Licensing tab.',
            {
              ENUMERATED_PRODUCT_NAMES: response.incompatibleProductIds
                ?.map(productId => this.translator.translateProductId(productId))
                .join(', '),
            }
          );
        }

        // show error toast
        this.showErrorToast(message);
      }
    } catch (exception) {
      // show error toast
      this.showErrorToast();
    } finally {
      // set loading state
      target.setLoadingState(false);
    }
  }

  private showComplianceManagerContactUsForm() {
    const complianceManagerContactUsForm = new InProductTrialsComplianceContactUs()

    complianceManagerContactUsForm.onSubmit = async ({ complianceForm }: { complianceForm: string }) => {
      try {
        // call start in product trial rpc
        const response = await this.dataRepository.requestInProductTrial(InProductTrialTypeEnum.ComplianceManager, complianceForm);
        
        if (response.success) {
          await this.refreshLandingPageForTrialType(InProductTrialTypeEnum.ComplianceManager);
          this.showSuccessToast(
            this.dependencies.translateFn('Request sent.')
          );
        } else {
          this.showErrorToast();
        }
      }
      catch (e) {
        this.showErrorToast();
      }
      
      dialog.close();
    };
    
    complianceManagerContactUsForm.onCancel = () => {
      dialog.close();
    };
    
    const dialogTitle = translateFn('Contact us');
    const dialogConfigs: GzDialog = {
      headline: dialogTitle,
      bodyContent: complianceManagerContactUsForm,
      isOpen: true,
      isWindow: false,
      closableOutsideBox: false,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);
  }

  private async requestInProductTrial(target: GzButtonWebComponent, type: AvailableProductTrialType) {
    try {
      if (type === InProductTrialTypeEnum.ComplianceManager) {
        this.showComplianceManagerContactUsForm();
        return;
      }

      // set loading state
      target.setLoadingState(true);

      // call start in product trial rpc
      const response = await this.dataRepository.requestInProductTrial(type);

      if (response.success) {
        // show success toast and refresh landing page
        await this.refreshLandingPageForTrialType(type);
        if (type === InProductTrialTypeEnum.ThreatIntel && response.companyAlreadyProvisioned) {
          this.showInfoToast(translateFn('Your company has an active integration with IntelliZone.'));
        }
        else {
          this.showSuccessToast(
            this.dependencies.translateFn('Thank you for your interest! We will be in touch shortly.')
          );
        }

        // keep disabled property value for button on success
        target.showSpinner = false;
      } else {
        // show error toast
        target.setLoadingState(false);
        this.showErrorToast();
      }
    } catch (exception) {
      // show error toast
      target.setLoadingState(false);
      this.showErrorToast();
    }
  }

  private showStopTrialConfirmationWindow(type: AvailableProductTrialType) {
    // body
    const dialogBodyHtml = `
      <p>
        ${this.dependencies.translateFn(
          'You will not be able to enroll in this product trial again for {coolDownPeriodMonths} months.',
          {
            coolDownPeriodMonths: CoolDownPeriodMonths,
          }
        )}
      </p>`;

    // buttons
    const dialogFooterHtml = `<gz-button id='confirm' type='full' color='primary' class='mr-2'>
       ${this.dependencies.translateFn('Confirm')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Cancel')}
      </gz-button>`;

    // title
    const windowTitle = this.dependencies.translateFn('Stop trial?');

    // confirmation callback
    const confirmCallback = async () => {
      const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

      if (confirmBtn) {
        confirmBtn.setLoadingState(true);
      }

      await this.cancelInProductTrial(type);
      dialog.close();
    };

    const dialog = GzDialogWebComponent.computeCustomDialog(
      dialogBodyHtml,
      dialogFooterHtml,
      windowTitle,
      null,
      confirmCallback,
      null,
      false
    );
  }

  private async cancelInProductTrial(type: AvailableProductTrialType) {
    try {
      // call cancel in product trial rpc
      const response = await this.dataRepository.cancelInProductTrial(type);

      if (response.success) {
        // show success toast and refresh landing page
        await this.refreshLandingPageForTrialType(type);
        this.showSuccessToast(
          this.dependencies.translateFn('{productName} trial ended.', {
            productName: getTranslatedInProductTrialType(type),
          })
        );

        this.reloadWindow();
      } else {
        // show error toast
        this.showErrorToast();
      }
    } catch (exception) {
      // show error toast
      this.showErrorToast();
    }
  }

  private openHowToLinkInProductTrial(type: AvailableProductTrialType) {
    window.open(getGzLandingPageProductLinkWithTracking(type, 'how-to'), '_blank');
  }

  private showDemoForInProductTrial(type: AvailableProductTrialType) {
    const link = GzLandingPageDemoLinks[type];
    if (!link) {
      return;
    }

    GzInProductTrialsVideoWebComponent.computeVideoComponent(link);
  }

  private showInfoToast(message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.INFORMATIVE,
    });
  }

  private showSuccessToast(message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showErrorToast(message?: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message ?? this.dependencies.translateFn('Request failed. Please refresh your console and try again.'),
      severity: ToastSeverity.ERROR,
    });
  }

  private reloadWindow() {
    setTimeout(function () {
      // remove before load event
      window.onbeforeunload = null;
      // force reload
      window.location.reload();
    }, 1500);
  }
}
